import { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '../../../components/hooks';
import { convertFromDesignToLayoutCoordinates } from '../../../library/layout-geometry/coordinates';
import {
  clearHoveredBearingEnvelopeEdge,
  clearHoveredBearingEnvelopeInterior,
} from '../../../store/actions/layout/bearingEnvelopeActions';
import {
  cancelActiveEditIfActive,
  commitActiveEdit,
  updateBearingEnvelopeEditPreview,
} from '../../../store/actions/layout/interactiveEditingActions';
import { isInteractiveEditActive } from '../../../store/selectors/interactiveEditingSelectors';
import { is3DLayoutCameraInUse } from '../../../store/selectors/layoutModeSelectors';
import { threeJsDom } from '../../util/events/ThreeJSDom';
import { GlobalThreeJSDomObjectEvent } from '../../util/events/types';
import { LEFT_MOUSE_BUTTON } from '../../util/events/util';

/**
 * Controller for global listeners for interactive bearing envelope behaviors.
 */
export function BearingEnvelopeInteractiveBehaviorsController() {
  const dispatch = useAppDispatch();

  const is3D = useAppSelector(is3DLayoutCameraInUse);
  const isEditActive = useAppSelector(isInteractiveEditActive);

  useEffect(() => {
    function onMouseMove(event: GlobalThreeJSDomObjectEvent) {
      if (is3D || !isEditActive) return;

      const worldPosition = convertFromDesignToLayoutCoordinates(event.eventPosition.worldPosition).projectTo2D();
      dispatch(updateBearingEnvelopeEditPreview(worldPosition));
    }

    function onMouseUp() {
      if (isEditActive) {
        dispatch(commitActiveEdit());
      }
    }

    function onMouseDown(event: GlobalThreeJSDomObjectEvent) {
      if (event.metadata.mouseButtons !== LEFT_MOUSE_BUTTON) {
        dispatch(cancelActiveEditIfActive());
      }
    }

    function onMouseLeave() {
      dispatch(cancelActiveEditIfActive());
      dispatch(clearHoveredBearingEnvelopeEdge());
      dispatch(clearHoveredBearingEnvelopeInterior());
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Control' || event.key === 'Shift') {
        return;
      }

      dispatch(cancelActiveEditIfActive());
    }

    function onWindowBlur() {
      dispatch(cancelActiveEditIfActive());
    }

    function onVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        dispatch(cancelActiveEditIfActive());
      }
    }

    threeJsDom.addGlobalListener('mousemove', onMouseMove);
    threeJsDom.addGlobalListener('mousedown', onMouseDown);
    threeJsDom.addGlobalListener('mouseup', onMouseUp);
    threeJsDom.addGlobalListener('mouseleave', onMouseLeave);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', onWindowBlur);

    return () => {
      threeJsDom.removeGlobalListener('mousemove', onMouseMove);
      threeJsDom.removeGlobalListener('mousedown', onMouseDown);
      threeJsDom.removeGlobalListener('mouseup', onMouseUp);
      threeJsDom.removeGlobalListener('mouseleave', onMouseLeave);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('blur', onWindowBlur);

      dispatch(clearHoveredBearingEnvelopeEdge());
      dispatch(clearHoveredBearingEnvelopeInterior());
    };
  }, [dispatch, is3D, isEditActive]);

  useEffect(() => () => void dispatch(cancelActiveEditIfActive()), [dispatch]);

  return null;
}
